import React, { useEffect, useState, useCallback, useRef } from "react";
import { Dialog } from "primereact/dialog";
import localeText from "../../../../../../locale";
import GetUserInfoService from "../../../../../../service/GetUserInfoService";
import { formatPrice } from "../../../../../../utilities/formatPrice";
import { formatDateTime } from "../../../../../../utilities/formatDate";
import InviteCouponDialogHeader from "./InviteCouponDialogHeader";
import InviteCouponStep1, { MIN_COUPON } from "./InviteCouponStep1";
import InviteCouponStep2 from "./InviteCouponStep2";
import "./InviteCouponDialog.css";

const service = new GetUserInfoService();

function resolveApiErrorMessage(res) {
  try {
    const list = localeText("data");
    if (Array.isArray(list)) {
      const item = list.find((m) => m.code === res.messageCode);
      if (item?.message) return item.message;
    }
  } catch {
    /* ignore */
  }
  return localeText("inviteCouponErrorGeneric");
}

const InviteCouponDialog = ({ visible, onHide, extracoinBalance, refreshData }) => {
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [success, setSuccess] = useState(null);
  const prevVisibleRef = useRef(false);
  const pendingRefreshRef = useRef(false);

  const reset = useCallback(() => {
    setStep(1);
    setAmount(null);
    setSubmitting(false);
    setFormError(null);
    setSuccess(null);
  }, []);

  useEffect(() => {
    if (!visible) {
      reset();
      prevVisibleRef.current = false;
      return;
    }
    if (!prevVisibleRef.current) {
      setStep(1);
      setSubmitting(false);
      setFormError(null);
      setSuccess(null);
      setAmount(extracoinBalance >= MIN_COUPON ? MIN_COUPON : null);
    }
    prevVisibleRef.current = true;
  }, [visible, reset, extracoinBalance]);

  const balanceFormatted = formatPrice(extracoinBalance);
  const debitedFormatted = success != null ? formatPrice(success.amount) : "";

  const handleClose = () => {
    if (pendingRefreshRef.current && typeof refreshData === "function") {
      refreshData();
    }
    pendingRefreshRef.current = false;
    reset();
    onHide();
  };

  const canSubmit =
    extracoinBalance >= MIN_COUPON &&
    amount != null &&
    Number(amount) >= MIN_COUPON &&
    Number(amount) <= extracoinBalance;

  const handleSubmit = async () => {
    setFormError(null);
    if (!canSubmit) return;
    const token = localStorage.getItem("__extraconomy_token");
    const username = localStorage.getItem("__extraconomy_username");
    if (!token || !username) {
      setFormError(localeText("inviteCouponErrorGeneric"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await service.postCouponInvite(token, Number(amount), username);
      if (res.messageCode === 200000 || res.messageCode === 200001) {
        const code = res.result?.number;
        if (code == null) {
          setFormError(localeText("inviteCouponErrorGeneric"));
          setSubmitting(false);
          return;
        }
        setSuccess({
          code: String(code),
          amount: Number(amount),
          at: Date.now(),
        });
        pendingRefreshRef.current = true;
        setStep(2);
      } else {
        setFormError(resolveApiErrorMessage(res));
      }
    } catch {
      setFormError(localeText("inviteCouponErrorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  const dateTimeLabel = success != null ? formatDateTime(success.at) : "";

  const shareHint =
    success != null ? localeText("inviteCouponShareHint", { couponValue: debitedFormatted }) : "";

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      header={null}
      className="invite-coupon-dialog"
      contentClassName="invite-coupon-dialog-content"
      headerClassName="hidden"
      modal
      dismissableMask={!submitting}
      closable={false}
      style={{ width: "min(100vw - 2rem, 28rem)" }}
    >
      <InviteCouponDialogHeader step={step} onClose={handleClose} />
      {step === 1 && (
        <InviteCouponStep1
          extracoinBalance={extracoinBalance}
          amount={amount}
          onAmountChange={setAmount}
          submitting={submitting}
          formError={formError}
          canSubmit={canSubmit}
          onCancel={handleClose}
          onSubmit={handleSubmit}
          balanceFormatted={balanceFormatted}
        />
      )}
      {step === 2 && success != null && (
        <InviteCouponStep2
          debitedFormatted={debitedFormatted}
          couponCode={success.code}
          dateTimeLabel={dateTimeLabel}
          shareHint={shareHint}
          onFinish={handleClose}
        />
      )}
    </Dialog>
  );
};

export default InviteCouponDialog;
